import {onUnknownChangeHandler} from 'lib/dom'
import Select from 'lib/ui/Select'
import TextField from 'lib/ui/TextField'
import Skeleton from '@material-ui/lab/Skeleton'
import React from 'react'
import styled from 'styled-components'
import Option from 'lib/ui/Select/Option'
import {spacing} from 'lib/ui/theme'
import {Widget, WidgetSources} from 'Event/Widget/template'

type SourcesFields = 'email' | 'firstName' | 'lastName'

export default function SourcesConfig(props: {
  widget: Widget | null
  updateWidget: (updatedFields: Partial<Widget>) => void
}) {
  const {widget, updateWidget} = props

  if (!widget) {
    return <Skeleton height={200} />
  }

  const handleSourceChange = (field: SourcesFields, source: WidgetSources) => {
    if (field === 'email') {
      updateWidget({email_source: source})
      return
    }

    if (field === 'firstName') {
      updateWidget({first_name_source: source})
      return
    }

    if (field === 'lastName') {
      updateWidget({last_name_source: source})
      return
    }
  }

  const handleKeyChange = (field: SourcesFields, key: string) => {
    if (field === 'email') {
      updateWidget({email_source_key: key})
      return
    }

    if (field === 'firstName') {
      updateWidget({first_name_source_key: key})
      return
    }

    if (field === 'lastName') {
      updateWidget({last_name_source_key: key})
      return
    }
  }

  return (
    <>
      <SourceHolder>
        <Select
          onChange={onUnknownChangeHandler((selected: WidgetSources) => {
            handleSourceChange('email', selected)
          })}
          value={widget.email_source || 'url'}
          variant="outlined"
          label={'Email Source'}
          aria-label="email source"
        >
          <Option aria-label="email url source" value="url">
            URL parameter
          </Option>
          <Option aria-label="email cookie source" value="cookie">
            Cookies
          </Option>
          <Option aria-label="email local storage source" value="local_storage">
            Local Storage
          </Option>
          <Option
            aria-label="email session storage source"
            value="session_storage"
          >
            Session Storage
          </Option>
          {/* <Option value="custom">Custom</Option> */}
        </Select>
        <div>
          {widget.email_source !== 'custom' && (
            <TextField
              value={widget.email_source_key}
              variant="outlined"
              label="Email Key (required)"
              inputProps={{
                'aria-label': 'email source key',
              }}
              required
              onChange={(value: string) => {
                handleKeyChange('email', value)
              }}
            ></TextField>
          )}
          {widget.email_source === 'custom' && (
            <CustomCodeHolder>
              You can add it with JS code: <br />
              <code>obvioWidget.addEmail('USER_EMAIL')</code>
            </CustomCodeHolder>
          )}
        </div>
      </SourceHolder>
      <SourceHolder>
        <Select
          onChange={onUnknownChangeHandler((selected: WidgetSources) => {
            handleSourceChange('firstName', selected)
          })}
          value={widget.first_name_source || 'url'}
          variant="outlined"
          label={'First Name Source'}
          aria-label="first name source"
        >
          <Option aria-label="first name url source" value="url">
            URL parameter
          </Option>
          <Option aria-label="first name cookie source" value="cookie">
            Cookies
          </Option>
          <Option
            aria-label="first name local storage source"
            value="local_storage"
          >
            Local Storage
          </Option>
          <Option
            aria-label="first name session storage source"
            value="session_storage"
          >
            Session Storage
          </Option>
          <Option aria-label="first name custom source" value="custom">
            Custom
          </Option>
        </Select>
        <div>
          {widget.first_name_source !== 'custom' && (
            <TextField
              value={widget.first_name_source_key}
              variant="outlined"
              label="First Name Key"
              onChange={(value: string) => {
                handleKeyChange('firstName', value)
              }}
              inputProps={{
                'aria-label': 'first name source key',
              }}
            ></TextField>
          )}
          {widget.first_name_source === 'custom' && (
            <CustomCodeHolder>
              You can add it with JS code: <br />
              <code>obvioWidget.addFirstName('USER_FIRST_NAME')</code>
            </CustomCodeHolder>
          )}
        </div>
      </SourceHolder>
      <SourceHolder>
        <Select
          onChange={onUnknownChangeHandler((selected: WidgetSources) => {
            handleSourceChange('lastName', selected)
          })}
          value={widget.last_name_source || 'url'}
          variant="outlined"
          label={'Last Name Source'}
          aria-label="last name source"
        >
          <Option aria-label="last name url source" value="url">
            URL parameter
          </Option>
          <Option aria-label="last name cookie source" value="cookie">
            Cookies
          </Option>
          <Option
            aria-label="last name local storage source"
            value="local_storage"
          >
            Local Storage
          </Option>
          <Option
            aria-label="last name session storage source"
            value="session_storage"
          >
            Session Storage
          </Option>
          <Option aria-label="last name custom source" value="custom">
            Custom
          </Option>
        </Select>
        <div>
          {widget.last_name_source !== 'custom' && (
            <TextField
              value={widget.last_name_source_key}
              variant="outlined"
              label="Last Name Key"
              onChange={(value: string) => {
                handleKeyChange('lastName', value)
              }}
              inputProps={{
                'aria-label': 'last name source key',
              }}
            ></TextField>
          )}
          {widget.last_name_source === 'custom' && (
            <CustomCodeHolder>
              You can add it with JS code: <br />
              <code>obvioWidget.addLastName('USER_LAST_NAME')</code>
            </CustomCodeHolder>
          )}
        </div>
      </SourceHolder>
    </>
  )
}

const SourceHolder = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};

  & > div {
    flex: 1;
  }
`

const CustomCodeHolder = styled.div`
  background: #f7f7f7;
  padding: 8px;
  border-radius: 4px;
  font-family: 'Courier New', Courier, monospace;
  height: 70px;
  display: flex;
  align-items: anchor-center;
  flex-direction: column;
  justify-content: center;
`
